import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Loader2, RotateCcw } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { API } from "../../App";
import { setNorthStarGuideCache } from "../../hooks/useNorthStar";
import { DEFAULT_GUIDE_SECTIONS } from "./spikingData";

// Admin dialog: reorder the guide's sections, hide them, collapse by default and set the text shown while collapsed. Site-wide.
export const NorthStarGuideEditor = ({ sections, onClose }) => {
  const [rows, setRows] = useState(sections.map((s) => ({ ...s })));
  const [saving, setSaving] = useState(false);
  const update = (i, patch) => setRows((r) => r.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const move = (i, dir) => setRows((r) => {
    const j = i + dir;
    if (j < 0 || j >= r.length) return r;
    const next = [...r];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/north-star-guide`, { sections: rows });
      setNorthStarGuideCache(r.data.sections);
      toast.success("Guide layout saved — every North Star card updated");
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    setSaving(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="north-star-guide-editor">
        <DialogHeader><DialogTitle>Arrange the North Star guide</DialogTitle></DialogHeader>
        <p className="text-xs text-[#5C5C5C]">Order, visibility and collapse state apply to every copy of the card across THE WALL. A collapsed section shows its title + the teaser text until the visitor taps “Show”.</p>
        <div className="space-y-3">
          {rows.map((s, i) => (
            <div key={s.key} className={`rounded-xl border p-3 ${s.enabled ? "border-[#E2E0DC] bg-white" : "border-dashed border-[#E2E0DC] bg-[#F9F8F6] opacity-70"}`} data-testid={`guide-row-${s.key}`}>
              <div className="flex items-start gap-2">
                <div className="flex flex-col gap-1">
                  <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="rounded border border-[#E2E0DC] p-1 disabled:opacity-30 hover:bg-[#F7F9FF]" data-testid={`guide-up-${s.key}`} aria-label="Move up"><ArrowUp className="h-3.5 w-3.5" /></button>
                  <button type="button" onClick={() => move(i, 1)} disabled={i === rows.length - 1} className="rounded border border-[#E2E0DC] p-1 disabled:opacity-30 hover:bg-[#F7F9FF]" data-testid={`guide-down-${s.key}`} aria-label="Move down"><ArrowDown className="h-3.5 w-3.5" /></button>
                </div>
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-extrabold uppercase tracking-widest text-[#1D4ED8] w-6">{i + 1}</span>
                    <input value={s.title} onChange={(e) => update(i, { title: e.target.value })} className="flex-1 rounded-md border border-[#E2E0DC] px-3 py-1.5 text-sm font-semibold" data-testid={`guide-title-${s.key}`} />
                  </div>
                  <div className="flex flex-wrap gap-x-5 gap-y-1 pl-8 text-xs font-bold text-[#1A1A1A]">
                    <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={s.enabled} onChange={(e) => update(i, { enabled: e.target.checked })} className="accent-[#1D4ED8]" data-testid={`guide-enabled-${s.key}`} /> Show this section</label>
                    <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={s.collapsed} onChange={(e) => update(i, { collapsed: e.target.checked })} className="accent-[#1D4ED8]" data-testid={`guide-collapsed-${s.key}`} /> Collapsed by default</label>
                  </div>
                  {s.collapsed && (
                    <textarea rows={2} value={s.teaser} onChange={(e) => update(i, { teaser: e.target.value })} placeholder="Text shown while collapsed (optional) — e.g. a one-line teaser. **bold** and [links](url) allowed." className="ml-8 w-[calc(100%-2rem)] rounded-md border border-[#E2E0DC] px-3 py-2 text-sm" data-testid={`guide-teaser-${s.key}`} />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => setRows(DEFAULT_GUIDE_SECTIONS.map((s) => ({ ...s })))} className="mr-auto text-xs" data-testid="guide-reset"><RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset to default</Button>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#1D4ED8] text-white" data-testid="guide-save">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save everywhere</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NorthStarGuideEditor;
